import React, { useState } from 'react';
import { useGetStravaActivitiesQuery } from '../slices/stravaApiSlice';  // Ensure this is the correct path to your API slice

// Helper function to get the Sunday of the week
const getStartOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day; // Adjust to get the previous Sunday
  const startOfWeek = new Date(d.setDate(diff));
  startOfWeek.setHours(0, 0, 0, 0); // Set time to midnight
  return startOfWeek;
};

// Group activities by week starting on Sunday
const groupByWeek = (activities) => {
  return activities.reduce((acc, activity) => {
    const startOfWeek = getStartOfWeek(activity.start_date).toLocaleDateString();
    if (!acc[startOfWeek]) {
      acc[startOfWeek] = [];
    }
    acc[startOfWeek].push(activity);
    return acc;
  }, {});
};

const StravaScreen = () => {
  
  // Use RTK Query hook to fetch activities from your backend
  const { data: activities, error, isLoading } = useGetStravaActivitiesQuery();
  
  const activitiesByWeek = groupByWeek(activities);
  const [openWeek, setOpenWeek] = useState(null); // Track which week is opened

  const toggleCollapse = (week) => {
    setOpenWeek(openWeek === week ? null : week); // Toggle the visibility of the week
  };

  if (isLoading) return <p>Loading activities...</p>;

  // Improved error handling
  if (error) {
    console.error('Error details:', error);  // Log the full error for debugging

    // Display detailed error message if available
    let errorMessage = 'Unknown error occurred';
    if (error.status === 'FETCH_ERROR') {
      errorMessage = 'Network error: Unable to reach the server.';
    } else if (error.status === 'PARSING_ERROR') {
      errorMessage = 'Error parsing response from server.';
    } else if (error.data?.message) {
      errorMessage = error.data.message;  // Display error message from server
    }

    return <p>Error loading activities: {errorMessage}</p>;
  }

  return (
    <div className="container">
      <h2 className="my-4">Strava Activities by Week</h2>
      <div className="accordion" id="activityAccordion">
        {Object.keys(activitiesByWeek).map((weekStart, index) => {
          const weeklyActivities = activitiesByWeek[weekStart];
          const totalDistance = weeklyActivities.reduce((total, activity) => total + activity.distance / 1609.34, 0);

          return (
            <div key={weekStart} className="card mb-3">
              <div className="card-header" id={`heading${index}`}>
                <h5 className="mb-0">
                  <button
                    className="btn btn-link"
                    type="button"
                    onClick={() => toggleCollapse(weekStart)}
                    aria-expanded={openWeek === weekStart}
                    aria-controls={`collapse${index}`}
                  >
                    Week Starting: {weekStart} (Total Distance: {totalDistance.toFixed(2)} miles)
                  </button>
                </h5>
              </div>
              <div
                id={`collapse${index}`}
                className={`collapse ${openWeek === weekStart ? 'show' : ''}`}
                aria-labelledby={`heading${index}`}
                data-parent="#activityAccordion"
              >
                <div className="card-body">
                  <table className="table table-striped table-bordered table-hover">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Distance (miles)</th>
                        <th>Moving Time (min)</th>
                        <th>Pace (min/mile)</th>
                        <th>Type</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {weeklyActivities.map((activity) => {
                        const distanceMiles = (activity.distance / 1609.34).toFixed(2);
                        const movingTimeMinutes = (activity.moving_time / 60).toFixed(0);
                        const pace = (activity.moving_time / 60 / (activity.distance / 1609.34)).toFixed(2); // minutes per mile

                        return (
                          <tr key={activity.name}>
                            <td>{activity.name}</td>
                            <td>{distanceMiles} miles</td>
                            <td>{movingTimeMinutes} min</td>
                            <td>{pace} min/mile</td>
                            <td>{activity.type}</td>
                            <td>{new Date(activity.start_date).toLocaleDateString()}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td><strong>Total</strong></td>
                        <td><strong>{totalDistance.toFixed(2)} miles</strong></td>
                        <td colSpan="4"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};


export default StravaScreen;